/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationsSendInvite } from "../funcs/organizationsSendInvite.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OrganizationInvitation } from "../models/components/organizationinvitation.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SendInviteRequest,
  SendInviteSecurity,
} from "../models/operations/sendinvite.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SendInviteMutationVariables = {
  request: SendInviteRequest;
  security?: SendInviteSecurity | undefined;
  options?: RequestOptions;
};

export type SendInviteMutationData = OrganizationInvitation;

export type SendInviteMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * sendInvite organizations
 *
 * @remarks
 * Send a WorkOS invitation for the active organization.
 */
export function useSendInviteMutation(
  options?: MutationHookOptions<
    SendInviteMutationData,
    SendInviteMutationError,
    SendInviteMutationVariables
  >,
): UseMutationResult<
  SendInviteMutationData,
  SendInviteMutationError,
  SendInviteMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSendInviteMutation(client, options),
    ...options,
  });
}

export function mutationKeySendInvite(): MutationKey {
  return ["@gram/client", "organizations", "sendInvite"];
}

export function buildSendInviteMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SendInviteMutationVariables,
  ) => Promise<SendInviteMutationData>;
} {
  return {
    mutationKey: mutationKeySendInvite(),
    mutationFn: function sendInviteMutationFn({
      request,
      security,
      options,
    }): Promise<SendInviteMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationsSendInvite(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
